"use client";

import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { axiosInstance } from "./axios";
import { queryClient } from "./query-client";

type LinkHubBlockLayout = "default" | "grid" | "large" | "compact";

type AuthState =
  | {
      user: User | null;
      status: "loading";
    }
  | {
      user: User;
      status: "authenticated";
      logout: () => void;
    }
  | {
      user: null;
      status: "unauthenticated";
    };

export function useAuth(options?: { initialData: User | null }): AuthState {
  const { user, isLoading, error } = useUser(options);

  const logout = async () => {
    await axiosInstance.post("/api/logout");
    queryClient.invalidateQueries();
    window.location.href = "/";
  };

  if (!isLoading && user && !error) {
    return {
      user,
      status: "authenticated",
      logout,
    };
  }

  if (isLoading) {
    return {
      user,
      status: "loading",
    };
  }

  return {
    user: null,
    status: "unauthenticated",
  };
}

export type User = {
  id: number;
  name: string;
  email: string;
  phone: string;
  phone_country_code: string;
  profile_photo_url: string;
  affiliate_code: string;
  link_hub_slug?: string | null;
  link_hub_slug_changed_at?: string | null;
  link_hub_slug_cooldown_until?: string | null;
  link_hub_enabled?: boolean;
  link_hub_block_layout?: LinkHubBlockLayout;
};

export function useUser(options?: { initialData: User | null }) {
  const { initialData } = options ?? {};

  const query = useQuery(
    ["auth", "user"],
    async () => {
      const { data } = await axiosInstance.get<User>("/api/me");
      return data;
    },
    {
      retry(failureCount, error) {
        if (error instanceof AxiosError && error?.response?.status === 401) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: true,
      initialData: initialData,
      onError(error) {
        if (error instanceof AxiosError && error?.response?.status === 401) {
          // Do nothing
          return;
        }
      },
    }
  );

  return {
    ...query,
    user: query.data ?? null,
  };
}
